import { Dispatch, SetStateAction } from 'react';

import { PersonOrgType } from '@ndvr/customer-api';

import { OwnerShape } from './account-model';
import { Chip, NativeSelect } from './fields';
import { PersonKind } from './onboarding-model';
import { OnboardingHousehold } from './useOnboardingHousehold';

/**
 * An account's owners as chips, with a select to add one from People (or a new person or entity) until the
 * registration has as many as it takes: two for a joint, one otherwise.
 */
export const OwnerPicker = ({
    shape,
    entityTypes,
    owners,
    onChange,
    household,
    addPerson,
}: {
    shape: OwnerShape;
    /** For an entity account, the kinds of entity it takes; others are not offered. */
    entityTypes?: PersonOrgType[];
    owners: string[];
    onChange: Dispatch<SetStateAction<string[]>>;
    household: OnboardingHousehold;
    addPerson: (kind: PersonKind, onCreated: (personOrgGUID: string) => void) => void;
}) => {
    const isEntity = shape === 'ENTITY';
    const fits = (personOrgGUID: string) => {
        const type = household.byGUID.get(personOrgGUID)?.personOrg.personOrgType;
        return !entityTypes || (!!type && entityTypes.includes(type));
    };
    const candidates = (
        isEntity ? household.peopleOf(PersonKind.ENTITY) : household.peopleOf(PersonKind.CLIENT, PersonKind.FAMILY)
    ).filter((option) => !owners.includes(option.value) && fits(option.value));

    return (
        <div className="flex flex-wrap items-center gap-2">
            {owners.map((guid) => (
                <Chip key={guid} onRemove={() => onChange(owners.filter((other) => other !== guid))}>
                    {household.nameOf(guid)}
                </Chip>
            ))}
            {owners.length < (shape === 'JOINT' ? 2 : 1) && (
                <NativeSelect
                    aria-label="Add owner"
                    fit
                    className="border-cocoa-300 h-8 rounded-full border-dashed text-[13px]"
                    value=""
                    placeholder={isEntity ? 'Add entity…' : 'Add person…'}
                    options={[...candidates, { value: 'new', label: isEntity ? 'Add new entity…' : 'Add new person…' }]}
                    onChange={(value) =>
                        value === 'new'
                            ? addPerson(isEntity ? PersonKind.ENTITY : PersonKind.CLIENT, (guid) =>
                                  onChange((current) => [...current, guid]),
                              )
                            : value && onChange([...owners, value])
                    }
                />
            )}
        </div>
    );
};
